import { useSessionValue } from '@portal/appLayer/auth';
import { CartRules } from '@portal/shared/utils/cartRules';
import { SSGates } from '@portal/state/context/StatsigContext/StatsigFlags';
import { useStoreValue } from '@portal/state/hooks/useStore';
import { useGateValue } from '@statsig/react-bindings';
import { useEffect, useState } from 'react';
import { isAgzProduct as isAgzProductByStore } from '@portal/shared/studio/ProductDetailPage/useSpecialMemberPricing';

interface CartRuleDiscounts {
  hasDiscount: boolean;
  discountedVariantId?: string;
  isLoading: boolean;
}

const cartRulesInstance = new CartRules();

export const useCartRuleDiscounts = (originalVariantId?: string): CartRuleDiscounts => {
  const [discounts, setDiscounts] = useState<CartRuleDiscounts>({
    hasDiscount: false,
    isLoading: true
  });

  const session = useSessionValue();
  const store = useStoreValue();

  // Check Statsig gate for AGZ SubscriptionActive discount
  const isAgzDiscountEnabled = useGateValue(SSGates.agzActiveSubscriptionDiscount);

  // Get customer tags - the cart rules already check for SubscriptionActive tag
  const customerTags = session?.customer?.tags;

  useEffect(() => {
    if (!originalVariantId || !store) {
      setDiscounts({ hasDiscount: false, isLoading: false });
      return;
    }

    // Prepare a Shopify-formatted variant ID for downstream API usage
    const formattedVariantId = originalVariantId.startsWith('gid://shopify/ProductVariant/')
      ? originalVariantId
      : `gid://shopify/ProductVariant/${originalVariantId}`;

    // Robust AGZ detection across stores/flavors using shared util
    const isAgzProduct = isAgzProductByStore(originalVariantId, store);

    // For AGZ products, only show discounts if flag is enabled
    if (isAgzProduct && !isAgzDiscountEnabled) {
      setDiscounts({ hasDiscount: false, isLoading: false });
      return;
    }

    const fetchDiscounts = async () => {
      setDiscounts((prev) => ({ ...prev, isLoading: true }));

      try {
        const discountedVariantId = await cartRulesInstance.getDiscountedVariantId(
          store,
          formattedVariantId,
          customerTags || undefined
        );

        const hasDiscount = !!discountedVariantId;

        setDiscounts({
          hasDiscount,
          discountedVariantId: discountedVariantId || undefined,
          isLoading: false
        });
      } catch (_error) {
        setDiscounts({ hasDiscount: false, isLoading: false });
      }
    };

    fetchDiscounts();
  }, [originalVariantId, store, customerTags, isAgzDiscountEnabled]);

  return discounts;
};
